<script lang="ts">
const MAX_ITEMS_PER_PAGE = 50;

const drivers = ['sessionStorage'];

const omitHeaders = [
  'security.ticker',
  'security.cusip',
  'renegotiateRate',
  'nextAllowedBuyInExecutionDate',
  'recalledQuantity',
  'displayId',
  'dtccRefId',
  'lenderDisplay',
  'borrowerDisplay',
  'independentAmount',
  'yesterdayIndependentAmount',
];
</script>

<script setup lang="ts">
import { computed, ref, shallowRef } from 'vue';
import LoanDetailsDialog from '@/modules/open-loans/components/LoanDetailsDialog.vue';
import { OpenLoan, Security } from '@/modules/common/models';
import OpenLoansList from '@/modules/open-loans/components/OpenLoansList.vue';
import SnapshotManager from '@/modules/common/components/SnapshotManager.vue';
import { SortModelItem } from 'ag-grid-enterprise';

const props = defineProps<{ security: Security }>();

const page = shallowRef(1);
const itemsPerPage = shallowRef(MAX_ITEMS_PER_PAGE);
const sort = shallowRef<SortModelItem>({ colId: 'createdAt', sort: 'desc' });

const detailLoan = shallowRef<OpenLoan | null>(null);
const loanDetailsDialog = ref(null);

const snapshot = computed((): Record<string, string> => {
  const snapshot: Record<string, string> = {
    sort: JSON.stringify(sort.value || { coldId: 'createdAt', sort: 'desc' }), // or 'asc'?
    page: page.value ? page.value.toString() : '1',
    itemsPerPage: itemsPerPage.value
      ? itemsPerPage.value.toString()
      : MAX_ITEMS_PER_PAGE.toString(),
  };
  if (props.security) {
    snapshot.cusip = props.security.cusip;
  }

  return snapshot;
});

function getStateMutators(): ReturnType<SnapshotManager['getStateMutators']> {
  return {
    sort: (value: string) => {
      sort.value = JSON.parse(value);
    },
    page: (value: string) => {
      page.value = parseInt(value, 10);
    },
    itemsPerPage: (value: string) => {
      itemsPerPage.value = parseInt(value, 10);
    },
  };
}

defineExpose({
  test: {
    detailLoan,
    loanDetailsDialog,
  },
});
</script>

<template>
  <VCard class="flex d-flex flex-column">
    <LoanDetailsDialog
      v-if="detailLoan"
      ref="loanDetailsDialog"
      :loan-id="detailLoan.id"
      @close-modal="detailLoan = null"
    />
    <SnapshotManager
      :drivers="drivers"
      :get-state-mutators="getStateMutators"
      :snapshot="snapshot"
      storage-key="snapshot-marketplace-symbol-overview-loans"
    >
      <OpenLoansList
        :counterparty="null"
        :detail-loan.sync="detailLoan"
        :items-per-page="itemsPerPage"
        :omit-headers="omitHeaders"
        :page.sync="page"
        :page-size="1000"
        :security="security"
        :show-dropdown-actions="false"
        :show-select="false"
        :show-table-filters="false"
        :show-tabs="false"
        :side="null"
        :sort.sync="sort"
      />
    </SnapshotManager>
  </VCard>
</template>
